import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Menu, X, Calculator, BookOpen, Target, Clock, 
  DollarSign, AlertCircle, ChevronRight, Trophy,
  GraduationCap, Users, TrendingUp, CheckCircle,
  ArrowRight, Info, BarChart3, Dumbbell, Eye, Activity,
  Mail, Copy, Check, Lightbulb, FileText, Send, UserCheck,
  Calendar, Video, Award, MessageSquare, Star, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home", icon: Target },
    { path: "/calculator", label: "Calculator", icon: Calculator },
    { path: "/coach-emails", label: "Coach Emails", icon: Mail },
    { path: "/resources/odds", label: "Recruiting Odds", icon: BarChart3 },
    { path: "/resources/timeline", label: "Timeline", icon: Clock },
    { path: "/resources/scholarships", label: "Scholarships", icon: DollarSign },
    { path: "/resources/myths", label: "Myth Busters", icon: AlertCircle },
    { path: "/how-it-works", label: "How It Works", icon: Info },
  ];

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <span className="heading-display text-2xl text-slate-900">RecruitFit</span>
            </Link>
          </div>

          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "bg-blue-50 text-blue-600"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-md ${
                  location.pathname === link.path
                    ? "bg-blue-50 text-blue-600"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <link.icon className="w-5 h-5" />
                <span className="font-medium">{link.label}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-slate-900 text-white py-12" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <span className="heading-display text-2xl">RecruitFit</span>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed">
            An educational resource helping student-athletes and families understand 
            realistic college recruiting pathways. Not a recruiting service.
          </p>
        </div>
        <div>
          <h4 className="heading-display text-lg mb-4">Resources</h4>
          <ul className="space-y-2 text-sm text-slate-400">
            <li><Link to="/resources/odds" className="hover:text-white">Recruiting Odds</Link></li>
            <li><Link to="/resources/timeline" className="hover:text-white">Timeline</Link></li>
            <li><Link to="/resources/scholarships" className="hover:text-white">Scholarships</Link></li>
            <li><Link to="/resources/myths" className="hover:text-white">Myth Busters</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="heading-display text-lg mb-4">Legal</h4>
          <ul className="space-y-2 text-sm text-slate-400">
            <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
            <li><Link to="/disclaimer" className="hover:text-white">Disclaimer</Link></li>
          </ul>
        </div>
      </div>
      <Separator className="my-8 bg-slate-700" />
      <div className="flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
        <p>© 2025 RecruitFit. Educational purposes only.</p>
        <p className="mt-2 md:mt-0">Data sourced from NCAA, NAIA, and public recruiting statistics.</p>
      </div>
    </div>
  </footer>
);

// Home Page Component
const HomePage = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-slate-50 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 playbook-texture opacity-30"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">
                Free Educational Tool
              </Badge>
              <h1 className="heading-italic text-5xl md:text-7xl text-slate-900 mb-6">
                Find Your <span className="text-blue-600">Realistic</span> Recruiting Level
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                An honest, data-driven tool to help high school student-athletes understand 
                where they might fit in college athletics. No sales pitch — just the facts.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button 
                    size="lg" 
                    className="btn-skew bg-blue-600 hover:bg-blue-700 text-white px-8 py-6"
                    data-testid="hero-calculator-btn"
                  >
                    <span className="flex items-center space-x-2">
                      <Calculator className="w-5 h-5" />
                      <span>Try the Calculator</span>
                    </span>
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="btn-skew border-2 border-slate-300 hover:border-blue-600 px-8 py-6"
                    data-testid="hero-how-btn"
                  >
                    <span>How It Works</span>
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fade-in-up animation-delay-200">
              <img 
                src="https://images.unsplash.com/photo-1700914298983-82538dd4d33e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="High school athletes on track"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Based on</p>
                    <p className="font-semibold text-slate-900">Real NCAA Data</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5 Categories Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-display text-4xl md:text-5xl text-slate-900 mb-4">
              Comprehensive 5-Factor Analysis
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our calculator evaluates you across five key recruiting dimensions.
            </p>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { icon: GraduationCap, title: "Academic", desc: "GPA, SAT, Class Rank", color: "blue" },
              { icon: Activity, title: "Competition", desc: "Sport-specific levels", color: "green" },
              { icon: Dumbbell, title: "Physical", desc: "Size & Strength", color: "orange" },
              { icon: Eye, title: "Exposure", desc: "Film, Camps, Outreach", color: "purple" },
              { icon: Trophy, title: "Performance", desc: "Impact & Recognition", color: "red" }
            ].map((cat, i) => (
              <Card key={i} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className={`w-12 h-12 mx-auto rounded-full bg-${cat.color}-100 flex items-center justify-center mb-3`}>
                    <cat.icon className={`w-6 h-6 text-${cat.color}-600`} />
                  </div>
                  <h3 className="heading-display text-lg">{cat.title}</h3>
                  <p className="text-sm text-slate-500">{cat.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reality Check Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="reality-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-display text-4xl md:text-5xl text-slate-900 mb-4">
              The Reality Check
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Most families overestimate recruiting opportunities. Here are the real numbers.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-l-4 border-l-red-500 bg-white">
              <CardHeader>
                <CardTitle className="heading-display text-6xl text-red-500">2%</CardTitle>
                <CardDescription className="text-lg text-slate-600">
                  of high school athletes play D1 sports
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-l-4 border-l-blue-500 bg-white">
              <CardHeader>
                <CardTitle className="heading-display text-6xl text-blue-500">7%</CardTitle>
                <CardDescription className="text-lg text-slate-600">
                  receive any athletic scholarship
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-l-4 border-l-green-500 bg-white">
              <CardHeader>
                <CardTitle className="heading-display text-6xl text-green-500">85%</CardTitle>
                <CardDescription className="text-lg text-slate-600">
                  of college athletes are walk-ons
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-display text-4xl md:text-5xl mb-6">
            Ready to Find Your <span className="text-blue-400">Recruiting Range</span>?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Our comprehensive calculator analyzes 5 key factors to estimate your realistic college athletic options.
          </p>
          <Link to="/calculator">
            <Button size="lg" className="btn-skew bg-blue-600 hover:bg-blue-700 px-12 py-6" data-testid="cta-calculator-btn">
              <span className="flex items-center space-x-2">
                <span>Start Calculator</span>
                <ArrowRight className="w-5 h-5" />
              </span>
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// Calculator Page Component - 5 Categories
const CalculatorPage = () => {
  const [step, setStep] = useState(0); // 0 = sport selection, 1-6 = categories, 7 = results
  const [loading, setLoading] = useState(false);
  const [sportsConfig, setSportsConfig] = useState({});
  const [competitionLevels, setCompetitionLevels] = useState([]);
  const [formOptions, setFormOptions] = useState({});
  const [sportStatsConfig, setSportStatsConfig] = useState({ stats: [] });
  const [results, setResults] = useState(null);
  
  const [formData, setFormData] = useState({
    sport: "",
    position: "",
    gender: "male",
    // Academic
    gpa: "",
    sat_score: "",
    class_rank: "",
    // Physical
    height_feet: "",
    height_inches: "",
    weight_lbs: "",
    bench_press: "",
    squat: "",
    deadlift: "",
    // Competition
    competition_level: "",
    // Recruiting
    coach_outreach: "",
    highlight_film: "",
    camps_showcases: "",
    recruiting_profiles: "",
    college_visits: "",
    // Performance
    playing_role: "",
    team_level: "",
    impact: "",
    recognition: "",
    coach_evaluation: "",
    sport_skill: "",
    // Sport-specific stats
    sport_stats: {}
  });

  const steps = [
    { title: "Sport & Position", icon: Trophy },
    { title: "Academic", icon: GraduationCap },
    { title: "Physical", icon: Dumbbell },
    { title: "Competition", icon: Activity },
    { title: "Recruiting", icon: Eye },
    { title: "Performance", icon: Target },
    { title: "Sport Stats", icon: TrendingUp },
    { title: "Results", icon: BarChart3 }
  ];

  useEffect(() => {
    fetchSportsConfig();
    fetchFormOptions();
  }, []);

  useEffect(() => {
    if (formData.sport) {
      fetchCompetitionLevels(formData.sport);
      fetchSportStatsConfig(formData.sport);
    }
  }, [formData.sport]);

  const fetchSportsConfig = async () => {
    try {
      const response = await axios.get(`${API}/sports`);
      setSportsConfig(response.data);
    } catch (error) {
      console.error("Error fetching sports:", error);
      toast.error("Failed to load sports configuration");
    }
  };

  const fetchCompetitionLevels = async (sport) => {
    try {
      const response = await axios.get(`${API}/competition-levels/${sport}`);
      setCompetitionLevels(response.data.levels);
    } catch (error) {
      console.error("Error fetching competition levels:", error);
    }
  };

  const fetchSportStatsConfig = async (sport) => {
    try {
      const response = await axios.get(`${API}/sport-stats/${sport}`);
      setSportStatsConfig(response.data);
    } catch (error) {
      console.error("Error fetching sport stats config:", error);
      setSportStatsConfig({ stats: [] });
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await axios.get(`${API}/form-options`);
      setFormOptions(response.data);
    } catch (error) {
      console.error("Error fetching form options:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === "sport") {
      setFormData(prev => ({ ...prev, position: "", competition_level: "", sport_skill: "", sport_stats: {} }));
    }
  };

  const handleStatChange = (statKey, value) => {
    setFormData(prev => ({
      ...prev,
      sport_stats: {
        ...prev.sport_stats,
        [statKey]: value
      }
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build sport stats payload - only include non-empty values
      const sportStatsPayload = {};
      Object.entries(formData.sport_stats).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          // Convert to number if it's a numeric stat
          const statConfig = sportStatsConfig.stats?.find(s => s.key === key);
          if (statConfig && statConfig.type === "number") {
            sportStatsPayload[key] = parseFloat(value);
          } else {
            sportStatsPayload[key] = value;
          }
        }
      });

      const payload = {
        sport: formData.sport,
        position: formData.position,
        gender: formData.gender,
        academic: {
          gpa: parseFloat(formData.gpa),
          sat_score: formData.sat_score ? parseInt(formData.sat_score) : null,
          class_rank: formData.class_rank || null
        },
        physical: {
          height_feet: parseInt(formData.height_feet),
          height_inches: parseInt(formData.height_inches) || 0,
          weight_lbs: parseInt(formData.weight_lbs),
          bench_press: formData.bench_press ? parseInt(formData.bench_press) : null,
          squat: formData.squat ? parseInt(formData.squat) : null,
          deadlift: formData.deadlift ? parseInt(formData.deadlift) : null
        },
        competition_level: formData.competition_level,
        recruiting: {
          coach_outreach: formData.coach_outreach,
          highlight_film: formData.highlight_film,
          camps_showcases: formData.camps_showcases,
          recruiting_profiles: formData.recruiting_profiles,
          college_visits: formData.college_visits
        },
        performance: {
          playing_role: formData.playing_role,
          team_level: formData.team_level,
          impact: formData.impact,
          recognition: formData.recognition || null,
          coach_evaluation: formData.coach_evaluation || null,
          sport_skill: formData.sport_skill
        },
        sport_stats: Object.keys(sportStatsPayload).length > 0 
          ? { stats: sportStatsPayload } 
          : null
      };

      const response = await axios.post(`${API}/calculate`, payload);
      setResults(response.data);
      setStep(7);
      toast.success("Calculation complete!");
    } catch (error) {
      console.error("Error calculating:", error);
      toast.error("Failed to calculate results. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return formData.sport && formData.position;
      case 1: return formData.gpa;
      case 2: return formData.height_feet && formData.weight_lbs;
      case 3: return formData.competition_level;
      case 4: return formData.coach_outreach && formData.highlight_film && formData.camps_showcases && formData.recruiting_profiles && formData.college_visits;
      case 5: return formData.playing_role && formData.team_level && formData.impact && formData.sport_skill;
      case 6: return true; // Sport stats are optional
      default: return false;
    }
  };

  const resetCalculator = () => {
    setStep(0);
    setResults(null);
    setFormData({
      sport: "", position: "", gender: "male",
      gpa: "", sat_score: "", class_rank: "",
      height_feet: "", height_inches: "", weight_lbs: "",
      bench_press: "", squat: "", deadlift: "",
      competition_level: "",
      coach_outreach: "", highlight_film: "", camps_showcases: "", recruiting_profiles: "", college_visits: "",
      playing_role: "", team_level: "", impact: "", recognition: "", coach_evaluation: "", sport_skill: "",
      sport_stats: {}
    });
    setSportStatsConfig({ stats: [] });
  };

  const positions = formData.sport && sportsConfig[formData.sport] 
    ? sportsConfig[formData.sport].positions 
    : [];

  const sportSkillOptions = formData.sport && formOptions.sport_skills 
    ? formOptions.sport_skills[formData.sport] 
    : [];

  return (
    <div className="min-h-screen bg-slate-50 py-8 md:py-12" data-testid="calculator-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="heading-display text-4xl md:text-5xl text-slate-900 mb-4">
            Recruiting Level Fit Calculator
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Complete all 5 sections for your personalized recruiting assessment.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8 overflow-x-auto pb-2">
          <div className="flex items-center space-x-2 min-w-max">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center">
                <div 
                  className={`flex flex-col items-center cursor-pointer transition-all ${
                    step === i ? "scale-110" : ""
                  }`}
                  onClick={() => i < step && setStep(i)}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    step > i ? "bg-green-500 text-white" :
                    step === i ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
                  }`}>
                    {step > i ? <CheckCircle className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs mt-1 hidden sm:block ${step === i ? "text-blue-600 font-semibold" : "text-slate-500"}`}>
                    {s.title}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-8 h-0.5 mx-1 ${step > i ? "bg-green-500" : "bg-slate-200"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-8">
            <Card className="shadow-lg">
              <CardContent className="p-6 md:p-8">
                
                {/* Step 0: Sport & Position */}
                {step === 0 && (
                  <div className="space-y-6 animate-fade-in-up" data-testid="step-0">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-blue-600" />
                      </div>
                      <h2 className="heading-display text-2xl text-slate-900">Select Your Sport</h2>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Sport *</Label>
                        <Select value={formData.sport} onValueChange={(v) => handleInputChange("sport", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="sport-select">
                            <SelectValue placeholder="Select your sport" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(sportsConfig).map(([key, config]) => (
                              <SelectItem key={key} value={key}>{config.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Position *</Label>
                        <Select 
                          value={formData.position} 
                          onValueChange={(v) => handleInputChange("position", v)}
                          disabled={!formData.sport}
                        >
                          <SelectTrigger className="h-12 mt-1" data-testid="position-select">
                            <SelectValue placeholder="Select your position" />
                          </SelectTrigger>
                          <SelectContent>
                            {positions.map((pos) => (
                              <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Gender *</Label>
                        <Select value={formData.gender} onValueChange={(v) => handleInputChange("gender", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="gender-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 1: Academic */}
                {step === 1 && (
                  <div className="space-y-6 animate-fade-in-up" data-testid="step-1-academic">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-blue-600" />
                      </div>
                      <h2 className="heading-display text-2xl text-slate-900">Academic Profile</h2>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Unweighted GPA (4.0 scale) *</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          min="0" 
                          max="4"
                          placeholder="e.g. 3.45"
                          value={formData.gpa}
                          onChange={(e) => handleInputChange("gpa", e.target.value)}
                          className="h-12 mt-1"
                          data-testid="gpa-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Enter your unweighted GPA. If your school only reports weighted GPA, enter your best estimate.
                        </p>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">SAT Score (Optional)</Label>
                        <Input 
                          type="number" 
                          min="400"
                          max="1600"
                          placeholder="e.g. 1280"
                          value={formData.sat_score}
                          onChange={(e) => handleInputChange("sat_score", e.target.value)}
                          className="h-12 mt-1"
                          data-testid="sat-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          If you haven't taken the SAT, leave this blank. GPA alone will be used.
                        </p>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Class Rank (Optional)</Label>
                        <Select value={formData.class_rank} onValueChange={(v) => handleInputChange("class_rank", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="class-rank-select">
                            <SelectValue placeholder="Select class rank" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.class_rank?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">
                          Select only if your school officially reports class rank.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Physical */}
                {step === 2 && (
                  <div className="space-y-6 animate-fade-in-up" data-testid="step-2-physical">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <Dumbbell className="w-5 h-5 text-orange-600" />
                      </div>
                      <h2 className="heading-display text-2xl text-slate-900">Physical Profile</h2>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Height *</Label>
                        <div className="grid grid-cols-2 gap-4 mt-1">
                          <div className="relative">
                            <Input 
                              type="number" 
                              min="4" max="7"
                              placeholder="5"
                              value={formData.height_feet}
                              onChange={(e) => handleInputChange("height_feet", e.target.value)}
                              className="h-12 pr-12"
                              data-testid="height-feet-input"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">ft</span>
                          </div>
                          <div className="relative">
                            <Input 
                              type="number" 
                              min="0" max="11"
                              placeholder="10"
                              value={formData.height_inches}
                              onChange={(e) => handleInputChange("height_inches", e.target.value)}
                              className="h-12 pr-12"
                              data-testid="height-inches-input"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">in</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Weight (lbs) *</Label>
                        <Input 
                          type="number" 
                          placeholder="e.g. 185"
                          value={formData.weight_lbs}
                          onChange={(e) => handleInputChange("weight_lbs", e.target.value)}
                          className="h-12 mt-1"
                          data-testid="weight-input"
                        />
                      </div>

                      <Separator />
                      <p className="text-sm text-slate-500">Strength metrics (optional) - most relevant for football and contact sports</p>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Bench Press (1RM)</Label>
                          <Input 
                            type="number" 
                            placeholder="e.g. 225"
                            value={formData.bench_press}
                            onChange={(e) => handleInputChange("bench_press", e.target.value)}
                            className="h-12 mt-1"
                            data-testid="bench-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Squat (1RM)</Label>
                          <Input 
                            type="number" 
                            placeholder="e.g. 315"
                            value={formData.squat}
                            onChange={(e) => handleInputChange("squat", e.target.value)}
                            className="h-12 mt-1"
                            data-testid="squat-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Deadlift (1RM)</Label>
                          <Input 
                            type="number" 
                            placeholder="e.g. 405"
                            value={formData.deadlift}
                            onChange={(e) => handleInputChange("deadlift", e.target.value)}
                            className="h-12 mt-1"
                            data-testid="deadlift-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Competition Level */}
                {step === 3 && (
                  <div className="space-y-6 animate-fade-in-up" data-testid="step-3-competition">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-green-600" />
                      </div>
                      <h2 className="heading-display text-2xl text-slate-900">Competition Level</h2>
                    </div>
                    
                    <p className="text-slate-600 mb-4">
                      Select your primary competition level for {sportsConfig[formData.sport]?.name || "your sport"}.
                    </p>
                    
                    <div className="space-y-2">
                      {competitionLevels.map((level) => (
                        <div 
                          key={level.value}
                          onClick={() => handleInputChange("competition_level", level.value)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            formData.competition_level === level.value 
                              ? "border-green-500 bg-green-50" 
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          data-testid={`comp-${level.value}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-700">{level.label}</span>
                            {formData.competition_level === level.value && (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-4">
                      Select the highest level you consistently compete at, not a one-time event.
                    </p>
                  </div>
                )}

                {/* Step 4: Recruiting & Exposure */}
                {step === 4 && (
                  <div className="space-y-6 animate-fade-in-up" data-testid="step-4-recruiting">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Eye className="w-5 h-5 text-purple-600" />
                      </div>
                      <h2 className="heading-display text-2xl text-slate-900">Recruiting & Exposure</h2>
                    </div>
                    
                    <p className="text-slate-600 mb-4">
                      These questions reflect how actively you are marketing yourself to college programs.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Coach Outreach Activity *</Label>
                        <Select value={formData.coach_outreach} onValueChange={(v) => handleInputChange("coach_outreach", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="coach-outreach-select">
                            <SelectValue placeholder="Select your outreach level" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.coach_outreach?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Highlight / Game Film *</Label>
                        <Select value={formData.highlight_film} onValueChange={(v) => handleInputChange("highlight_film", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="film-select">
                            <SelectValue placeholder="Select your film status" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.highlight_film?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Camps / Showcases Attended *</Label>
                        <Select value={formData.camps_showcases} onValueChange={(v) => handleInputChange("camps_showcases", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="camps-select">
                            <SelectValue placeholder="Select camps/showcases" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.camps_showcases?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Recruiting Profiles *</Label>
                        <Select value={formData.recruiting_profiles} onValueChange={(v) => handleInputChange("recruiting_profiles", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="profiles-select">
                            <SelectValue placeholder="Select profile status" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.recruiting_profiles?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">College Visits / Interest *</Label>
                        <Select value={formData.college_visits} onValueChange={(v) => handleInputChange("college_visits", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="visits-select">
                            <SelectValue placeholder="Select visit status" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.college_visits?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 5: Performance */}
                {step === 5 && (
                  <div className="space-y-6 animate-fade-in-up" data-testid="step-5-performance">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <Target className="w-5 h-5 text-red-600" />
                      </div>
                      <h2 className="heading-display text-2xl text-slate-900">Performance & Skill Level</h2>
                    </div>
                    
                    <p className="text-slate-600 mb-4">
                      Answer honestly based on your most recent full season.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Your Role on Your Team *</Label>
                        <Select value={formData.playing_role} onValueChange={(v) => handleInputChange("playing_role", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="role-select">
                            <SelectValue placeholder="Select your role" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.playing_role?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Team Strength *</Label>
                        <Select value={formData.team_level} onValueChange={(v) => handleInputChange("team_level", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="team-level-select">
                            <SelectValue placeholder="How strong is your team?" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.team_level?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Your Impact in Games *</Label>
                        <Select value={formData.impact} onValueChange={(v) => handleInputChange("impact", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="impact-select">
                            <SelectValue placeholder="Select your impact level" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.impact?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Recognition (Optional)</Label>
                        <Select value={formData.recognition} onValueChange={(v) => handleInputChange("recognition", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="recognition-select">
                            <SelectValue placeholder="Select any recognition" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.recognition?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">Coach Feedback (Optional)</Label>
                        <Select value={formData.coach_evaluation} onValueChange={(v) => handleInputChange("coach_evaluation", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="coach-eval-select">
                            <SelectValue placeholder="Coach feedback level" />
                          </SelectTrigger>
                          <SelectContent>
                            {formOptions.coach_evaluation?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          {sportsConfig[formData.sport]?.name} Skill Level *
                        </Label>
                        <Select value={formData.sport_skill} onValueChange={(v) => handleInputChange("sport_skill", v)}>
                          <SelectTrigger className="h-12 mt-1" data-testid="sport-skill-select">
                            <SelectValue placeholder="Select your skill level" />
                          </SelectTrigger>
                          <SelectContent>
                            {sportSkillOptions?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 6: Sport-Specific Stats (Optional) */}
                {step === 6 && (
                  <div className="space-y-6 animate-fade-in-up" data-testid="step-6-stats">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h2 className="heading-display text-2xl text-slate-900">Sport-Specific Stats</h2>
                        <p className="text-sm text-slate-500">Optional but recommended for more accurate results</p>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
                      <p className="text-sm text-blue-700">
                        <strong>Pro tip:</strong> Adding your measurable stats helps compare your performance directly to division benchmarks. 
                        If you don't know these numbers, you can skip this step.
                      </p>
                    </div>
                    
                    {sportStatsConfig.stats && sportStatsConfig.stats.length > 0 ? (
                      <div className="space-y-4">
                        {sportStatsConfig.stats
                          .filter(stat => 
                            stat.positions.includes("all") || stat.positions.includes(formData.position)
                          )
                          .map((stat) => (
                            <div key={stat.key}>
                              <Label className="text-sm font-medium text-slate-700">{stat.label}</Label>
                              {stat.type === "number" ? (
                                <div className="relative">
                                  <Input 
                                    type="number" 
                                    step={stat.key === "batting_average" ? "0.001" : "0.1"}
                                    min={stat.min}
                                    max={stat.max}
                                    placeholder={`e.g. ${stat.benchmarks?.D2 || ""}`}
                                    value={formData.sport_stats[stat.key] || ""}
                                    onChange={(e) => handleStatChange(stat.key, e.target.value)}
                                    className="h-12 mt-1"
                                    data-testid={`stat-${stat.key}`}
                                  />
                                  <div className="mt-1 text-xs text-slate-500 flex justify-between">
                                    <span>D1: {stat.benchmarks?.D1}</span>
                                    <span>D2: {stat.benchmarks?.D2}</span>
                                    <span>D3: {stat.benchmarks?.D3}</span>
                                  </div>
                                </div>
                              ) : stat.type === "select" ? (
                                <Select 
                                  value={formData.sport_stats[stat.key] || ""} 
                                  onValueChange={(v) => handleStatChange(stat.key, v)}
                                >
                                  <SelectTrigger className="h-12 mt-1" data-testid={`stat-${stat.key}`}>
                                    <SelectValue placeholder="Select level" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {stat.options?.map((opt) => (
                                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : null}
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <p>No sport-specific stats available for {sportsConfig[formData.sport]?.name}.</p>
                        <p className="text-sm mt-2">You can proceed to calculate your results.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 7: Results */}
                {step === 7 && results && (
                  <div className="space-y-6 animate-fade-in-up" data-testid="step-7-results">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="heading-display text-2xl text-slate-900">Your Recruiting Fit Results</h2>
                      <Button variant="outline" onClick={resetCalculator} data-testid="reset-btn">
                        Start Over
                      </Button>
                    </div>

                    {/* Primary Summary */}
                    <div className="p-5 bg-slate-900 text-white rounded-xl">
                      <p className="text-lg leading-relaxed">{results.primary_summary}</p>
                    </div>

                    {/* Fit Scores by Level */}
                    <div>
                      <h3 className="font-semibold text-slate-700 mb-4">Fit Score by Level</h3>
                      <div className="space-y-3">
                        {results.fit_scores.map((fit) => {
                          const getColorClass = (score) => {
                            if (score >= 80) return { bg: "bg-green-500", text: "text-green-700", light: "bg-green-50", border: "border-green-200" };
                            if (score >= 65) return { bg: "bg-blue-500", text: "text-blue-700", light: "bg-blue-50", border: "border-blue-200" };
                            if (score >= 45) return { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50", border: "border-amber-200" };
                            if (score >= 25) return { bg: "bg-orange-500", text: "text-orange-700", light: "bg-orange-50", border: "border-orange-200" };
                            return { bg: "bg-slate-400", text: "text-slate-600", light: "bg-slate-50", border: "border-slate-200" };
                          };
                          const colors = getColorClass(fit.score);
                          
                          return (
                            <div key={fit.level} className={`p-4 rounded-lg ${colors.light} border ${colors.border}`} data-testid={`fit-${fit.level.toLowerCase().replace(/\s+/g, '-')}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-slate-800">{fit.level}</span>
                                <div className="flex items-center space-x-3">
                                  <Badge className={colors.bg}>{fit.label}</Badge>
                                  <span className="text-2xl font-bold text-slate-900">{fit.score}<span className="text-sm text-slate-500">/100</span></span>
                                </div>
                              </div>
                              <Progress value={fit.score} className="h-2 mb-2" />
                              <p className="text-xs text-slate-600">{fit.description}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <Separator />

                    {/* Why These Results */}
                    <div>
                      <h3 className="font-semibold text-slate-700 mb-3">Why These Results</h3>
                      <ul className="space-y-2">
                        {results.why_these_results.map((reason, i) => (
                          <li key={i} className="flex items-start space-x-2 text-sm text-slate-600">
                            <ChevronRight className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Separator />

                    {/* Category Scores */}
                    <div>
                      <h3 className="font-semibold text-slate-700 mb-4">Your Category Scores</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        {results.category_scores.map((cat) => (
                          <div key={cat.name} className="p-4 bg-slate-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-slate-900">{cat.percentage}%</p>
                            <p className="text-xs text-slate-500 mt-1">{cat.name}</p>
                            <Progress value={cat.percentage} className="h-1 mt-2" />
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-xs text-green-600 uppercase tracking-wide">Strongest Area</p>
                          <p className="font-semibold text-green-800">{results.strongest_category}</p>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-xs text-amber-600 uppercase tracking-wide">Area to Develop</p>
                          <p className="font-semibold text-amber-800">{results.weakest_category}</p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* How to Improve */}
                    <div>
                      <h3 className="heading-display text-xl text-slate-900 mb-4">How to Improve Your Recruiting Fit</h3>
                      <div className="space-y-4">
                        {results.improvement_actions.map((action, i) => (
                          <Card key={i} className="border-l-4 border-l-blue-500" data-testid={`improvement-${i}`}>
                            <CardHeader className="pb-2">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-lg">{action.title}</CardTitle>
                                <Badge variant="outline" className="text-blue-600 border-blue-300">{action.impact}</Badge>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              <div>
                                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Why This Matters</p>
                                <p className="text-sm text-slate-600">{action.why_it_matters}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">What to Do Next</p>
                                <ul className="space-y-1">
                                  {action.what_to_do.map((todo, j) => (
                                    <li key={j} className="flex items-start space-x-2 text-sm text-slate-600">
                                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                      <span>{todo}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="p-2 bg-blue-50 rounded-md">
                                <p className="text-xs text-blue-700">
                                  <span className="font-semibold">Improves:</span> {action.what_it_improves}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {/* School Matches */}
                    {results.school_matches && results.school_matches.length > 0 && (
                      <>
                        <Separator />
                        <div data-testid="school-matches-section">
                          <h3 className="heading-display text-xl text-slate-900 mb-4">Potential School Matches</h3>
                          <p className="text-sm text-slate-500 mb-4">
                            Based on your profile, here are schools that may be a good fit for your athletic and academic background.
                          </p>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {results.school_matches.map((school, i) => (
                              <Card key={i} className="hover:shadow-md transition-shadow" data-testid={`school-match-${i}`}>
                                <CardContent className="p-4">
                                  <div className="flex items-start justify-between mb-2">
                                    <h4 className="font-semibold text-slate-800 text-sm">{school.name}</h4>
                                    <Badge variant="outline" className={
                                      school.division === "D1" ? "border-blue-300 text-blue-700" :
                                      school.division === "D2" ? "border-green-300 text-green-700" :
                                      school.division === "D3" ? "border-purple-300 text-purple-700" :
                                      "border-orange-300 text-orange-700"
                                    }>
                                      {school.division}
                                    </Badge>
                                  </div>
                                  <div className="space-y-1 text-xs text-slate-600">
                                    <p><span className="text-slate-400">Location:</span> {school.state}</p>
                                    <p><span className="text-slate-400">Conference:</span> {school.conference}</p>
                                    <p><span className="text-slate-400">Size:</span> {school.size} | {school.setting}</p>
                                  </div>
                                  <div className="mt-3 pt-2 border-t">
                                    <p className="text-xs text-slate-500">{school.fit_reason}</p>
                                    <p className="text-xs text-green-600 mt-1">{school.academic_match}</p>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-xs text-amber-700">
                              <strong>Note:</strong> This is a representative sample of schools. 
                              Research each program's specific requirements and reach out to coaches directly.
                            </p>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Stats Analysis */}
                    {results.stats_analysis && results.stats_analysis.stats_provided && results.stats_analysis.stats_provided.length > 0 && (
                      <>
                        <Separator />
                        <div data-testid="stats-analysis-section">
                          <h3 className="heading-display text-xl text-slate-900 mb-4">Your Stats Analysis</h3>
                          <div className="space-y-3">
                            {results.stats_analysis.stats_provided.map((stat, i) => (
                              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div>
                                  <p className="font-medium text-slate-700">{stat.stat}</p>
                                  <p className="text-sm text-slate-500">Your value: {stat.value}</p>
                                </div>
                                <div className="text-right">
                                  <Badge className={
                                    stat.assessment === "D1 level" ? "bg-blue-500" :
                                    stat.assessment === "D2 level" ? "bg-green-500" :
                                    stat.assessment === "D3 level" ? "bg-purple-500" :
                                    "bg-slate-400"
                                  }>
                                    {stat.assessment || "N/A"}
                                  </Badge>
                                  <p className="text-xs text-slate-400 mt-1">D1: {stat.d1_benchmark}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Disclaimer */}
                    <div className="p-4 bg-slate-100 rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-500 text-center">{results.disclaimer}</p>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                {step < 7 && (
                  <div className="flex justify-between mt-8 pt-6 border-t">
                    <Button 
                      variant="outline" 
                      onClick={() => setStep(s => s - 1)}
                      disabled={step === 0}
                      data-testid="prev-btn"
                    >
                      Previous
                    </Button>
                    {step < 6 ? (
                      <Button 
                        onClick={() => setStep(s => s + 1)}
                        disabled={!canProceed()}
                        data-testid="next-btn"
                      >
                        Next Step
                      </Button>
                    ) : (
                      <Button 
                        onClick={handleSubmit}
                        disabled={!canProceed() || loading}
                        data-testid="submit-btn"
                      >
                        {loading ? "Calculating..." : "Calculate Fit"}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <Card className="bg-slate-900 text-white sticky top-24" data-testid="progress-sidebar">
              <CardHeader>
                <CardTitle className="heading-display text-lg">Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {steps.slice(0, 7).map((s, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      step > i ? "bg-green-500" : step === i ? "bg-blue-500" : "bg-slate-700"
                    }`}>
                      {step > i ? <CheckCircle className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-sm ${step === i ? "font-semibold" : "text-slate-400"}`}>{s.title}</span>
                  </div>
                ))}
                <Separator className="bg-slate-700" />
                <p className="text-xs text-slate-500">
                  This calculator provides estimates only. Results do not guarantee recruiting outcomes.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Recruiting Odds Page
const OddsPage = () => {
  const [odds, setOdds] = useState(null);
  const [selectedSport, setSelectedSport] = useState("basketball");

  useEffect(() => {
    const fetchOdds = async () => {
      try {
        const response = await axios.get(`${API}/recruiting-odds`);
        setOdds(response.data);
      } catch (error) {
        console.error("Error fetching odds:", error);
      }
    };
    fetchOdds();
  }, []);

  const sportOdds = odds && odds[selectedSport];

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="odds-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="heading-display text-4xl md:text-5xl text-slate-900 mb-4">
            Recruiting Odds by Division
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Understanding the real percentages helps set realistic expectations.
          </p>
        </div>

        <div className="flex justify-center mb-8 overflow-x-auto pb-2">
          <Tabs value={selectedSport} onValueChange={setSelectedSport} className="w-full max-w-2xl">
            <TabsList className="grid grid-cols-4 md:grid-cols-8 w-full min-w-max" data-testid="sport-tabs">
              <TabsTrigger value="basketball" className="text-xs md:text-sm">Basketball</TabsTrigger>
              <TabsTrigger value="football" className="text-xs md:text-sm">Football</TabsTrigger>
              <TabsTrigger value="soccer" className="text-xs md:text-sm">Soccer</TabsTrigger>
              <TabsTrigger value="baseball" className="text-xs md:text-sm">Baseball</TabsTrigger>
              <TabsTrigger value="lacrosse" className="text-xs md:text-sm">Lacrosse</TabsTrigger>
              <TabsTrigger value="volleyball" className="text-xs md:text-sm">Volleyball</TabsTrigger>
              <TabsTrigger value="softball" className="text-xs md:text-sm">Softball</TabsTrigger>
              <TabsTrigger value="cheerleading" className="text-xs md:text-sm">Cheer</TabsTrigger>
              <TabsTrigger value="track" className="text-xs md:text-sm">Track</TabsTrigger>
              <TabsTrigger value="wrestling" className="text-xs md:text-sm">Wrestling</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {sportOdds && (
          <div className="grid md:grid-cols-2 gap-8">
            {Object.entries(sportOdds).map(([gender, data]) => (
              <Card key={gender} className="shadow-lg" data-testid={`odds-card-${gender}`}>
                <CardHeader>
                  <CardTitle className="heading-display text-2xl capitalize">{gender}</CardTitle>
                  <CardDescription>
                    Percentage of high school athletes who compete at each level
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {["D1", "D2", "D3", "NAIA", "JUCO"].map((div) => (
                      <div key={div}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{div}</span>
                          <span className="text-sm text-slate-600">{data[div]}%</span>
                        </div>
                        <Progress value={data[div] * 10} className="h-2" />
                      </div>
                    ))}
                    <Separator />
                    <div className="flex justify-between">
                      <span className="font-semibold text-slate-700">Total College Athletes</span>
                      <span className="font-bold text-blue-600">{data.total}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card className="mt-8 bg-yellow-50 border-yellow-200">
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-yellow-800 mb-1">Important Context</h3>
                <p className="text-sm text-yellow-700">
                  These percentages represent the proportion of high school athletes who go on to compete 
                  at each NCAA division level. They do not represent the odds of receiving a scholarship. 
                  Source: NCAA Research.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Timeline Page
const TimelinePage = () => {
  const timelineData = [
    {
      grade: "Freshman (9th Grade)",
      focus: "Foundation Building",
      academic: ["Focus on core subjects", "Aim for 3.0+ GPA", "Take challenging courses"],
      athletic: ["Develop fundamentals", "Play on JV/freshman team", "Consider club sports"],
      recruiting: ["Learn NCAA rules", "Start college research", "Too early for contact"]
    },
    {
      grade: "Sophomore (10th Grade)",
      focus: "Skill Development",
      academic: ["Maintain GPA", "Take PSATs", "Research eligibility"],
      athletic: ["Compete for varsity", "Attend camps", "Start highlight video"],
      recruiting: ["Register with NCAA", "Research programs", "Attend college games"]
    },
    {
      grade: "Junior (11th Grade)",
      focus: "Prime Recruiting Year",
      academic: ["Most important year", "Take SAT/ACT", "Complete core courses"],
      athletic: ["Prime exposure season", "Attend ID camps", "Update highlights"],
      recruiting: ["Coaches can contact you", "Reach out to coaches", "Schedule visits"]
    },
    {
      grade: "Senior (12th Grade)",
      focus: "Decision Time",
      academic: ["Maintain GPA", "Complete requirements", "Apply to colleges"],
      athletic: ["Continue performing", "Update senior film", "All-star games"],
      recruiting: ["Official visits", "Signing Day", "Consider walk-ons"]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="timeline-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="heading-display text-4xl md:text-5xl text-slate-900 mb-4">
            Grade-by-Grade Timeline
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A roadmap for each year of high school.
          </p>
        </div>

        <div className="space-y-8">
          {timelineData.map((year, index) => (
            <Card key={year.grade} className="shadow-lg overflow-hidden" data-testid={`timeline-${index}`}>
              <div className="brand-stripe h-2" />
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
                    {9 + index}
                  </div>
                  <div>
                    <CardTitle className="heading-display text-2xl">{year.grade}</CardTitle>
                    <CardDescription className="text-lg">{year.focus}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { title: "Academic", items: year.academic, icon: GraduationCap, color: "blue" },
                    { title: "Athletic", items: year.athletic, icon: Trophy, color: "green" },
                    { title: "Recruiting", items: year.recruiting, icon: Target, color: "red" }
                  ].map((section) => (
                    <div key={section.title}>
                      <h3 className={`flex items-center space-x-2 font-semibold text-${section.color}-700 mb-3`}>
                        <section.icon className="w-5 h-5" />
                        <span>{section.title}</span>
                      </h3>
                      <ul className="space-y-2">
                        {section.items.map((item, i) => (
                          <li key={i} className="flex items-start space-x-2 text-sm text-slate-600">
                            <CheckCircle className={`w-4 h-4 text-${section.color}-500 mt-0.5 flex-shrink-0`} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// Scholarships Page
const ScholarshipsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="scholarships-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="heading-display text-4xl md:text-5xl text-slate-900 mb-4">
            Scholarship Reality
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Understanding how athletic scholarships actually work.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className="shadow-lg" data-testid="headcount-card">
            <CardHeader className="bg-blue-50">
              <CardTitle className="heading-display text-2xl text-blue-900">Headcount Sports</CardTitle>
              <CardDescription>Full scholarships only</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-slate-600 mb-4">
                In headcount sports, each scholarship counts as one full scholarship. Coaches cannot divide scholarships.
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Men's (D1)</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Football (FBS): 85 scholarships</li>
                    <li>• Basketball: 13 scholarships</li>
                  </ul>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Women's (D1)</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Basketball: 15 scholarships</li>
                    <li>• Volleyball: 12 scholarships</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg" data-testid="equivalency-card">
            <CardHeader className="bg-green-50">
              <CardTitle className="heading-display text-2xl text-green-900">Equivalency Sports</CardTitle>
              <CardDescription>Scholarships can be divided</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-slate-600 mb-4">
                In equivalency sports, coaches can divide scholarships among multiple athletes.
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Common Examples</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Baseball: 11.7 scholarships for 27+ roster</li>
                    <li>• Soccer (Men): 9.9 scholarships</li>
                    <li>• Soccer (Women): 14 scholarships</li>
                  </ul>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-700">
                    Most athletes receive partial scholarships (25-75% of costs).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="shadow-lg" data-testid="walkons-card">
          <CardHeader>
            <CardTitle className="heading-display text-2xl">The Walk-On Path</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-slate-50 rounded-lg">
                <p className="text-4xl font-bold text-blue-600 mb-2">85%</p>
                <p className="text-slate-600">of college athletes are walk-ons or receive partial scholarships</p>
              </div>
              <div className="text-center p-6 bg-slate-50 rounded-lg">
                <p className="text-4xl font-bold text-green-600 mb-2">50%+</p>
                <p className="text-slate-600">of D3 athletes walked on to their teams</p>
              </div>
              <div className="text-center p-6 bg-slate-50 rounded-lg">
                <p className="text-4xl font-bold text-red-600 mb-2">Many</p>
                <p className="text-slate-600">walk-ons earn scholarships after proving themselves</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Myths Page
const MythsPage = () => {
  const myths = [
    { myth: "If you're good enough, coaches will find you", reality: "Coaches are busy. You must actively market yourself.", tip: "Create highlight videos and reach out to coaches directly." },
    { myth: "Only D1 matters", reality: "D1 is ~2% of athletes. D2, D3, NAIA offer excellent opportunities.", tip: "Evaluate schools based on fit, not prestige." },
    { myth: "I need to commit early", reality: "Early commitments are non-binding. Many athletes commit junior/senior year.", tip: "Take your time and make an informed decision." },
    { myth: "Full scholarships are common", reality: "Full rides are rare in equivalency sports. Most are partial.", tip: "Academic scholarships often exceed athletic aid." },
    { myth: "My club coach will get me recruited", reality: "Club coaches can help, but YOU must drive the process.", tip: "Own your recruitment and reach out to coaches yourself." },
    { myth: "I'm too short to play in college", reality: "Skill, work ethic, and game IQ often matter more than size.", tip: "Focus on what you can control: skills and academics." }
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="myths-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="heading-display text-4xl md:text-5xl text-slate-900 mb-4">
            Recruiting Myth Busters
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Common misconceptions that can derail your recruiting journey.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {myths.map((item, index) => (
            <AccordionItem key={index} value={`myth-${index}`} className="bg-white rounded-lg shadow-sm border" data-testid={`myth-${index}`}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-start space-x-4 text-left">
                  <Badge variant="destructive" className="mt-1">MYTH</Badge>
                  <span className="font-semibold text-slate-900">{item.myth}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="ml-16 space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <Badge className="bg-green-600 mb-2">REALITY</Badge>
                    <p className="text-slate-700">{item.reality}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Badge className="bg-blue-600 mb-2">TIP</Badge>
                    <p className="text-slate-700">{item.tip}</p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

// How It Works Page
const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="how-it-works-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="heading-display text-4xl md:text-5xl text-slate-900 mb-4">
            How This Calculator Works
          </h1>
          <p className="text-lg text-slate-600">
            Transparency about our methodology.
          </p>
        </div>

        <div className="space-y-8">
          <Card className="shadow-lg" data-testid="methodology-card">
            <CardHeader>
              <CardTitle className="heading-display text-2xl">5-Factor Analysis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600">
                Our calculator evaluates athletes across five key dimensions:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "Academic", desc: "GPA, test scores, and class rank determine academic eligibility and fit" },
                  { title: "Competition Level", desc: "Sport-specific levels (AAU, ECNL, Showcases) indicate exposure quality" },
                  { title: "Physical Profile", desc: "Height, weight, and strength relative to position benchmarks" },
                  { title: "Recruiting Activity", desc: "Outreach, film, camps, and visits show initiative" },
                  { title: "Performance", desc: "Playing role, team level, impact, and recognition" }
                ].map((item) => (
                  <div key={item.title} className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg" data-testid="limitations-card">
            <CardHeader>
              <CardTitle className="heading-display text-2xl">Limitations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-slate-600">
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <span>Cannot evaluate actual athletic ability or potential</span>
                </li>
                <li className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <span>Does not account for team needs or coaching preferences</span>
                </li>
                <li className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <span>Benchmarks are averages and vary by program</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Disclaimer Page
const DisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="disclaimer-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="heading-display text-4xl md:text-5xl text-slate-900 mb-4">
            Disclaimer
          </h1>
        </div>

        <Card className="shadow-lg">
          <CardContent className="p-8 space-y-6 text-slate-600">
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
              <h2 className="font-bold text-red-800 mb-3">Important Notice</h2>
              <p className="text-red-700">
                RecruitFit is an educational resource only. We are NOT a recruiting service, 
                scouting agency, or college placement service.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-3">No Guarantees</h3>
              <p>
                Results are for educational purposes only and do not guarantee any recruiting outcomes.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Not Professional Advice</h3>
              <p>
                Always verify information with official NCAA, NAIA, or institutional sources.
              </p>
            </div>

            <Separator />

            <p className="text-sm text-slate-500 text-center">
              By using RecruitFit, you acknowledge this disclaimer. Last updated: January 2025.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============ COACH EMAIL TEMPLATES PAGE ============

const EMAIL_TEMPLATES = {
  initial_introduction: {
    name: "Initial Introduction",
    icon: Send,
    description: "First contact with a college coach - make a strong first impression",
    when: "When you first reach out to a program you're interested in",
    tips: [
      "Keep it concise (under 200 words)",
      "Lead with your most impressive credential",
      "Include a specific reason why you're interested in THEIR program",
      "Attach or link your highlight video",
      "Include your contact information and social/recruiting profiles"
    ],
    subjectTemplate: "[Grad Year] [Position] - [Your Name] - Interested in [School] [Sport]",
    bodyTemplate: `Dear Coach [Coach Last Name],

My name is [Your Name], and I am a [Graduation Year] [Position] at [High School Name] in [City, State]. I am reaching out because I am very interested in the [Sport] program at [College Name].

[1-2 sentences about why you're specifically interested in their program - be genuine and specific]

Here are my current credentials:
• GPA: [GPA] | [SAT/ACT if applicable]
• [Key Athletic Achievement 1]
• [Key Athletic Achievement 2]
• [Key Stat or Measurable]

I have attached my highlight video for your review: [Link]

You can also find more information about me on:
• [Recruiting Profile Link]
• [Social Media if appropriate]

I would welcome the opportunity to learn more about your program and discuss how I might contribute to [College Name] [Sport]. I will be attending [Upcoming Camp/Event if applicable].

Thank you for your time and consideration.

Sincerely,
[Your Name]
[Phone Number]
[Email Address]
[High School] Class of [Year]`
  },
  follow_up: {
    name: "Follow-Up Email",
    icon: MessageSquare,
    description: "Check in after your initial email or a previous interaction",
    when: "2-3 weeks after initial contact with no response, or after an event/update",
    tips: [
      "Reference your previous email or interaction",
      "Add NEW information (updated stats, recent achievement, new video)",
      "Keep it shorter than your initial email",
      "Don't be pushy or desperate",
      "Show continued genuine interest"
    ],
    subjectTemplate: "Following Up - [Your Name] [Position] [Grad Year]",
    bodyTemplate: `Dear Coach [Coach Last Name],

I hope this email finds you well. I wanted to follow up on my previous email from [Date/Timeframe] and share some updates.

Since my last contact:
• [New Achievement or Update 1]
• [New Achievement or Update 2]
• [Updated Stat if applicable]

I continue to be very interested in [College Name] because [specific reason]. [If applicable: I noticed [something recent about the program - recent game, news, etc.]]

I have updated my highlight video with recent game footage: [Link]

I would still love the opportunity to speak with you about the program. I am available [general availability] if you have time for a brief call.

Thank you again for your consideration.

Best regards,
[Your Name]
[Phone Number]
[Email Address]`
  },
  post_camp: {
    name: "Post-Camp Thank You",
    icon: Award,
    description: "Send within 24-48 hours after attending a college camp or clinic",
    when: "Immediately after attending a camp, clinic, or unofficial visit",
    tips: [
      "Send within 24-48 hours while you're fresh in their memory",
      "Reference specific feedback or conversations you had",
      "Mention something specific you learned or enjoyed",
      "Reiterate your interest in the program",
      "If you received specific feedback, show you're working on it"
    ],
    subjectTemplate: "Thank You - [Your Name] from [Camp Name/Date]",
    bodyTemplate: `Dear Coach [Coach Last Name],

Thank you so much for the opportunity to participate in the [Camp Name/Clinic] this past [Day/Weekend]. I truly enjoyed the experience and learned a great deal.

I particularly appreciated [specific drill, feedback, or interaction]. Your coaching on [specific technique or concept] was extremely helpful, and I am already working to implement what I learned.

[If you received specific feedback: You mentioned that I should work on [specific area]. I want you to know I am committed to improving in this area and will [specific action you're taking].]

The experience reinforced my strong interest in [College Name]. [Mention something specific about the campus, facilities, team culture, or academic programs that impressed you.]

I hope to have the opportunity to continue demonstrating my abilities and commitment to you. Please let me know if there are any other camps or opportunities to visit campus.

Thank you again for your time and investment in my development.

Sincerely,
[Your Name]
[Phone Number]
[Email Address]`
  },
  highlight_video: {
    name: "New Highlight Video",
    icon: Video,
    description: "Share updated game footage or a new highlight reel",
    when: "When you have significant new footage that showcases improvement",
    tips: [
      "Only send when you have genuinely new, quality footage",
      "Keep highlight videos under 3-5 minutes",
      "Put your best plays first (coaches may not watch the whole thing)",
      "Include game footage, not just practice",
      "Add clear labels (jersey number, position, game date)"
    ],
    subjectTemplate: "Updated Highlights - [Your Name] [Position] [Grad Year]",
    bodyTemplate: `Dear Coach [Coach Last Name],

I hope you are doing well. I wanted to share my updated highlight video featuring footage from [recent season/tournament/games].

Updated Highlight Video: [Link]

This video includes:
• [Type of footage - varsity games, tournament, etc.]
• [Number of clips or minutes]
• [What skills/plays are featured]

Key stats from this footage:
• [Relevant stat 1]
• [Relevant stat 2]

My jersey number is [#] and I am the [brief physical description if helpful].

I remain very interested in [College Name] and would welcome any feedback you might have after reviewing the footage.

Thank you for your time.

Best regards,
[Your Name]
[Phone Number]
[Email Address]`
  },
  academic_update: {
    name: "Academic Update",
    icon: GraduationCap,
    description: "Share improved grades, test scores, or academic achievements",
    when: "After receiving new grades, test scores, or academic honors",
    tips: [
      "Academics matter - coaches need to know you can get admitted",
      "Share GPA updates at the end of each semester",
      "Include test score improvements",
      "Mention academic honors or challenging courses",
      "Show you take academics seriously"
    ],
    subjectTemplate: "Academic Update - [Your Name] [Grad Year]",
    bodyTemplate: `Dear Coach [Coach Last Name],

I wanted to share an update on my academic progress as I know this is an important factor in the recruiting process.

Academic Update:
• Current GPA: [GPA] (previously [old GPA if improved])
• [SAT/ACT Score if new or improved]
• [Class Rank if applicable]
• [Relevant courses: AP classes, honors, etc.]

[If applicable: I recently [academic achievement - honor roll, academic award, etc.]]

I am taking a rigorous course load this [semester/year] including [challenging courses relevant to intended major if known].

I understand the importance of academic success both for admission and for being a successful student-athlete at [College Name]. I am committed to maintaining strong grades while continuing to develop athletically.

Please let me know if you need any additional academic information or transcripts.

Thank you,
[Your Name]
[Phone Number]
[Email Address]`
  },
  season_update: {
    name: "Season/Stats Update",
    icon: TrendingUp,
    description: "Share current season performance and statistics",
    when: "Mid-season or end-of-season to share your performance",
    tips: [
      "Focus on your most impressive and relevant stats",
      "Include team success if applicable",
      "Mention any awards or recognition",
      "Be honest - don't exaggerate",
      "Compare to previous seasons to show improvement"
    ],
    subjectTemplate: "[Season Year] Season Update - [Your Name] [Position]",
    bodyTemplate: `Dear Coach [Coach Last Name],

I wanted to update you on my [current/recent] season with [Team Name].

Season Statistics:
• [Key Stat 1]: [Number]
• [Key Stat 2]: [Number]
• [Key Stat 3]: [Number]
• Games/Matches Played: [Number]

[If applicable: Compared to last season, I have improved my [stat] from [old] to [new].]

Team Accomplishments:
• Current Record: [W-L]
• [Tournament results, league standing, etc.]

Individual Recognition:
• [Any awards, all-conference, all-state nominations, etc.]

[Optional: Brief note about what you've been working on or focusing on]

I continue to be very interested in [College Name] and would love the opportunity to contribute to your program.

Updated highlights from this season: [Link if available]

Thank you for following my progress.

Best regards,
[Your Name]
[Phone Number]
[Email Address]`
  },
  continued_interest: {
    name: "Expression of Continued Interest",
    icon: Star,
    description: "Reaffirm your interest in a program you haven't heard from",
    when: "When a school is a top choice but communication has gone quiet",
    tips: [
      "Be genuine about why they're a top choice",
      "Don't be desperate or pushy",
      "Include any new information since last contact",
      "Ask a specific question to encourage response",
      "Show you've done your research on the program"
    ],
    subjectTemplate: "[College Name] Remains a Top Choice - [Your Name]",
    bodyTemplate: `Dear Coach [Coach Last Name],

I hope your [season/off-season] is going well. [If applicable: Congratulations on [recent team achievement or news].]

I wanted to reach out to express my continued strong interest in [College Name]. Your program remains one of my top choices because [2-3 specific, genuine reasons - academics, coaching philosophy, team culture, location, etc.].

Since we last communicated:
• [Update 1]
• [Update 2]

I have been researching [College Name] extensively and am particularly drawn to [specific academic program, facility, tradition, or aspect of the program].

I would welcome any opportunity to visit campus, attend a practice, or speak with you about the program. I am also planning to attend [upcoming event/camp if applicable].

Is there anything specific you would like to see from me, or any feedback you can share about where I stand in your recruiting process?

Thank you for your time and consideration.

Respectfully,
[Your Name]
[Phone Number]
[Email Address]`
  },
  questionnaire_follow_up: {
    name: "After Completing Questionnaire",
    icon: FileText,
    description: "Follow up after filling out a program's recruiting questionnaire",
    when: "Within a few days of completing an online recruiting questionnaire",
    tips: [
      "Reference that you completed their questionnaire",
      "Briefly highlight your top credentials",
      "Show you did more than just fill out a form",
      "Express genuine interest",
      "Make it easy for them to find your info"
    ],
    subjectTemplate: "Questionnaire Completed + Introduction - [Your Name] [Grad Year] [Position]",
    bodyTemplate: `Dear Coach [Coach Last Name],

I recently completed the recruiting questionnaire on the [College Name] [Sport] website and wanted to personally introduce myself.

My name is [Your Name], a [Graduation Year] [Position] from [High School] in [City, State].

Quick highlights:
• GPA: [GPA] | [Test scores if strong]
• [Top athletic credential]
• [Key measurable or stat]

I am genuinely interested in [College Name] because [specific reason about academics or athletics].

Highlight Video: [Link]
Recruiting Profile: [Link]

I would welcome any opportunity to learn more about your program and discuss how I might contribute.

Thank you for your consideration.

Sincerely,
[Your Name]
[Phone Number]
[Email Address]`
  }
};

const EMAIL_TIPS = {
  general: [
    { title: "Personalize Every Email", description: "Never send generic mass emails. Coaches can tell. Reference specific things about their program." },
    { title: "Proofread Everything", description: "Spelling errors and wrong coach/school names are immediate red flags. Triple-check before sending." },
    { title: "Use a Professional Email", description: "Create an email like firstname.lastname@gmail.com. Avoid nicknames or numbers." },
    { title: "Include Your Info in Every Email", description: "Name, graduation year, position, phone, high school - make it easy for coaches to identify you." },
    { title: "Keep It Concise", description: "Coaches receive hundreds of emails. Get to the point quickly - aim for under 200 words." },
    { title: "Be Genuine", description: "Don't just say you're interested - explain WHY. Generic flattery is obvious and ineffective." }
  ],
  subjectLines: [
    { title: "Always Include Key Info", description: "Your graduation year, position, and name should be in the subject line." },
    { title: "Be Specific", description: "Bad: 'Interested in your program' | Good: '2026 PG - John Smith - Interested in Duke Basketball'" },
    { title: "Match Your Purpose", description: "If it's a follow-up, say so. If you're sharing video, mention it." }
  ],
  timing: [
    { title: "Best Days", description: "Tuesday through Thursday typically have the best response rates." },
    { title: "Best Times", description: "Early morning (7-9 AM) or early afternoon (1-3 PM) in the coach's time zone." },
    { title: "Avoid Busy Periods", description: "Don't email during games, tournaments, or signing periods unless urgent." },
    { title: "Follow-Up Timing", description: "Wait 2-3 weeks before following up. Don't email daily or weekly." }
  ],
  donts: [
    { title: "Don't Exaggerate", description: "Coaches will verify your stats and credentials. Dishonesty ends recruiting relationships." },
    { title: "Don't Be Pushy", description: "Asking 'Where do I stand?' repeatedly is a turn-off. Let the process unfold." },
    { title: "Don't Badmouth Others", description: "Never speak negatively about other schools, coaches, or players." },
    { title: "Don't Have Parents Write", description: "The email should come from YOU. Parents can help edit, but you must write it." },
    { title: "Don't Send Attachments", description: "Use links to videos and profiles. Attachments often get blocked or ignored." },
    { title: "Don't Mass Email", description: "Coaches talk. They know when they receive the same email as 50 other schools." }
  ]
};

const CoachEmailsPage = () => {
  const [selectedTemplate, setSelectedTemplate] = useState("initial_introduction");
  const [copied, setCopied] = useState(false);
  const [copiedSubject, setCopiedSubject] = useState(false);
  const [formData, setFormData] = useState({
    athleteName: "",
    graduationYear: "",
    position: "",
    sport: "",
    highSchool: "",
    city: "",
    state: "",
    gpa: "",
    testScore: "",
    coachName: "",
    collegeName: "",
    highlightLink: "",
    profileLink: "",
    phone: "",
    email: "",
    achievement1: "",
    achievement2: "",
    keyStat: "",
    whyInterested: ""
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateEmail = () => {
    const template = EMAIL_TEMPLATES[selectedTemplate];
    let body = template.bodyTemplate;
    let subject = template.subjectTemplate;
    
    // Replace placeholders
    const replacements = {
      "[Your Name]": formData.athleteName || "[Your Name]",
      "[Graduation Year]": formData.graduationYear || "[Graduation Year]",
      "[Grad Year]": formData.graduationYear || "[Grad Year]",
      "[Position]": formData.position || "[Position]",
      "[Sport]": formData.sport || "[Sport]",
      "[High School Name]": formData.highSchool || "[High School Name]",
      "[High School]": formData.highSchool || "[High School]",
      "[City, State]": formData.city && formData.state ? `${formData.city}, ${formData.state}` : "[City, State]",
      "[City]": formData.city || "[City]",
      "[State]": formData.state || "[State]",
      "[GPA]": formData.gpa || "[GPA]",
      "[SAT/ACT if applicable]": formData.testScore || "[SAT/ACT if applicable]",
      "[SAT/ACT Score if new or improved]": formData.testScore || "[SAT/ACT Score]",
      "[Coach Last Name]": formData.coachName || "[Coach Last Name]",
      "[College Name]": formData.collegeName || "[College Name]",
      "[School]": formData.collegeName || "[School]",
      "[Link]": formData.highlightLink || "[Link]",
      "[Recruiting Profile Link]": formData.profileLink || "[Recruiting Profile Link]",
      "[Phone Number]": formData.phone || "[Phone Number]",
      "[Email Address]": formData.email || "[Email Address]",
      "[Key Athletic Achievement 1]": formData.achievement1 || "[Key Athletic Achievement 1]",
      "[Key Athletic Achievement 2]": formData.achievement2 || "[Key Athletic Achievement 2]",
      "[Key Stat or Measurable]": formData.keyStat || "[Key Stat or Measurable]",
      "[Year]": formData.graduationYear || "[Year]",
      "[Team Name]": formData.highSchool || "[Team Name]",
      "[#]": "[#]"
    };

    Object.entries(replacements).forEach(([key, value]) => {
      body = body.split(key).join(value);
      subject = subject.split(key).join(value);
    });

    return { subject, body };
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'subject') {
      setCopiedSubject(true);
      setTimeout(() => setCopiedSubject(false), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    toast.success(`${type === 'subject' ? 'Subject line' : 'Email'} copied to clipboard!`);
  };

  const template = EMAIL_TEMPLATES[selectedTemplate];
  const { subject: generatedSubject, body: generatedBody } = generateEmail();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="coach-emails-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center">
              <Mail className="w-8 h-8" />
            </div>
          </div>
          <h1 className="heading-display text-4xl sm:text-5xl mb-4">Coach Email Templates</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Professional, proven email templates to help you communicate effectively with college coaches. 
            Personalize, copy, and send with confidence.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Quick Tips Banner */}
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-900">Pro Tip: Quality Over Quantity</p>
                <p className="text-sm text-blue-700">
                  Send 10 thoughtful, personalized emails to schools you genuinely want to attend, 
                  rather than 100 generic emails. Coaches can immediately tell the difference.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Template Selection & Form */}
          <div className="lg:col-span-1 space-y-6">
            {/* Template Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Template</CardTitle>
                <CardDescription>Choose the right email for your situation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(EMAIL_TEMPLATES).map(([key, tmpl]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedTemplate(key)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedTemplate === key 
                        ? "border-blue-500 bg-blue-50 shadow-sm" 
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`template-${key}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        selectedTemplate === key ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-600"
                      }`}>
                        <tmpl.icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${selectedTemplate === key ? "text-blue-900" : "text-slate-700"}`}>
                          {tmpl.name}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Your Information Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Information</CardTitle>
                <CardDescription>Fill in to personalize your email</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Your Name</Label>
                    <Input 
                      placeholder="John Smith" 
                      value={formData.athleteName}
                      onChange={(e) => handleInputChange("athleteName", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Grad Year</Label>
                    <Input 
                      placeholder="2026" 
                      value={formData.graduationYear}
                      onChange={(e) => handleInputChange("graduationYear", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Sport</Label>
                    <Input 
                      placeholder="Basketball" 
                      value={formData.sport}
                      onChange={(e) => handleInputChange("sport", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Position</Label>
                    <Input 
                      placeholder="Point Guard" 
                      value={formData.position}
                      onChange={(e) => handleInputChange("position", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">High School</Label>
                  <Input 
                    placeholder="Lincoln High School" 
                    value={formData.highSchool}
                    onChange={(e) => handleInputChange("highSchool", e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">City</Label>
                    <Input 
                      placeholder="Austin" 
                      value={formData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">State</Label>
                    <Input 
                      placeholder="TX" 
                      value={formData.state}
                      onChange={(e) => handleInputChange("state", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">GPA</Label>
                    <Input 
                      placeholder="3.8" 
                      value={formData.gpa}
                      onChange={(e) => handleInputChange("gpa", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">SAT/ACT</Label>
                    <Input 
                      placeholder="1250" 
                      value={formData.testScore}
                      onChange={(e) => handleInputChange("testScore", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
                <Separator />
                <div>
                  <Label className="text-xs">Coach's Last Name</Label>
                  <Input 
                    placeholder="Johnson" 
                    value={formData.coachName}
                    onChange={(e) => handleInputChange("coachName", e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">College Name</Label>
                  <Input 
                    placeholder="University of Texas" 
                    value={formData.collegeName}
                    onChange={(e) => handleInputChange("collegeName", e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <Separator />
                <div>
                  <Label className="text-xs">Highlight Video Link</Label>
                  <Input 
                    placeholder="https://hudl.com/..." 
                    value={formData.highlightLink}
                    onChange={(e) => handleInputChange("highlightLink", e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Recruiting Profile Link</Label>
                  <Input 
                    placeholder="https://ncsa.com/..." 
                    value={formData.profileLink}
                    onChange={(e) => handleInputChange("profileLink", e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input 
                      placeholder="(555) 123-4567" 
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input 
                      placeholder="john.smith@email.com" 
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
                <Separator />
                <div>
                  <Label className="text-xs">Key Achievement 1</Label>
                  <Input 
                    placeholder="All-District First Team 2024" 
                    value={formData.achievement1}
                    onChange={(e) => handleInputChange("achievement1", e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Key Achievement 2</Label>
                  <Input 
                    placeholder="Team Captain, 20-5 record" 
                    value={formData.achievement2}
                    onChange={(e) => handleInputChange("achievement2", e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Key Stat/Measurable</Label>
                  <Input 
                    placeholder="15.2 PPG, 5.4 APG" 
                    value={formData.keyStat}
                    onChange={(e) => handleInputChange("keyStat", e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Generated Email & Tips */}
          <div className="lg:col-span-2 space-y-6">
            {/* Template Info */}
            <Card className="border-slate-300">
              <CardHeader className="pb-2">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <template.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>{template.name}</CardTitle>
                    <CardDescription>{template.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2 text-sm text-slate-600 mb-4">
                  <Calendar className="w-4 h-4" />
                  <span><strong>When to use:</strong> {template.when}</span>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-amber-800 mb-2">Tips for this template:</p>
                  <ul className="text-sm text-amber-700 space-y-1">
                    {template.tips.map((tip, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Subject Line */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Subject Line</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 p-3 bg-slate-100 rounded-lg font-mono text-sm">
                    {generatedSubject}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(generatedSubject, 'subject')}
                    data-testid="copy-subject-btn"
                  >
                    {copiedSubject ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Generated Email */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Generated Email</CardTitle>
                  <Button 
                    onClick={() => copyToClipboard(generatedBody, 'body')}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="copy-email-btn"
                  >
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied ? "Copied!" : "Copy Email"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-white border rounded-lg p-4 font-mono text-sm whitespace-pre-wrap leading-relaxed max-h-[500px] overflow-y-auto" data-testid="generated-email">
                  {generatedBody}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  <strong>Note:</strong> Replace any remaining [brackets] with your specific information before sending.
                </p>
              </CardContent>
            </Card>

            {/* Email Best Practices */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  <span>Email Best Practices</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="general" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="general" className="text-xs">General</TabsTrigger>
                    <TabsTrigger value="subjects" className="text-xs">Subject Lines</TabsTrigger>
                    <TabsTrigger value="timing" className="text-xs">Timing</TabsTrigger>
                    <TabsTrigger value="donts" className="text-xs">Don'ts</TabsTrigger>
                  </TabsList>
                  <TabsContent value="general" className="mt-4">
                    <div className="grid md:grid-cols-2 gap-3">
                      {EMAIL_TIPS.general.map((tip, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-lg">
                          <p className="font-medium text-sm text-slate-800">{tip.title}</p>
                          <p className="text-xs text-slate-600 mt-1">{tip.description}</p>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="subjects" className="mt-4">
                    <div className="space-y-3">
                      {EMAIL_TIPS.subjectLines.map((tip, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-lg">
                          <p className="font-medium text-sm text-slate-800">{tip.title}</p>
                          <p className="text-xs text-slate-600 mt-1">{tip.description}</p>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="timing" className="mt-4">
                    <div className="grid md:grid-cols-2 gap-3">
                      {EMAIL_TIPS.timing.map((tip, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-lg">
                          <p className="font-medium text-sm text-slate-800">{tip.title}</p>
                          <p className="text-xs text-slate-600 mt-1">{tip.description}</p>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="donts" className="mt-4">
                    <div className="grid md:grid-cols-2 gap-3">
                      {EMAIL_TIPS.donts.map((tip, i) => (
                        <div key={i} className="p-3 bg-red-50 rounded-lg border border-red-100">
                          <p className="font-medium text-sm text-red-800">{tip.title}</p>
                          <p className="text-xs text-red-600 mt-1">{tip.description}</p>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* NCAA Rules Reminder */}
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-900">NCAA Contact Rules Reminder</p>
                    <p className="text-sm text-amber-700 mt-1">
                      NCAA rules vary by sport and division regarding when coaches can respond to you. 
                      Don't be discouraged if you don't hear back immediately - coaches may be in a "quiet period" 
                      or "dead period" where contact is limited. You can always reach out; restrictions apply to coaches, not athletes.
                    </p>
                    <a 
                      href="https://www.ncaa.org/sports/2014/10/13/recruiting-calendars.aspx" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-amber-800 underline mt-2 inline-block hover:text-amber-900"
                    >
                      View NCAA Recruiting Calendars →
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App min-h-screen bg-slate-50">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/calculator" element={<CalculatorPage />} />
          <Route path="/coach-emails" element={<CoachEmailsPage />} />
          <Route path="/resources/odds" element={<OddsPage />} />
          <Route path="/resources/timeline" element={<TimelinePage />} />
          <Route path="/resources/scholarships" element={<ScholarshipsPage />} />
          <Route path="/resources/myths" element={<MythsPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
        </Routes>
        <Footer />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
